import { Component } from "react";
import styled from "styled-components";

// Utils
import { rem, Color, Opacity } from "../../utils/style";
import { authenticate, getSupportMailTo } from "../../utils/authenticate";
import { redirectReplaceHistory } from "../../utils/redirect";
import { emailRegex } from "../../utils/helpers";
import metrics from "../../utils/metrics";
import intl from "../../services/intl";
import SHA from "js-sha256";

// Components
import RitualSubmitButton from "../global/RitualSubmitButton";
import Text from "../Text";
import MagicLink from "../MagicLink";

// Styled Elements
const Form = styled.form.attrs({
  name: "login",
})`
  legend {
    display: none;
  }
`;

const InputGroup = styled.div`
  margin-bottom: 32px;

  &.error {
    margin-bottom: 12px;
  }

  &:last-of-type {
    margin-bottom: 16px;
  }
`;

const PasswordLink = styled(MagicLink)`
  font-size: ${rem(14)};
  line-height: ${rem(24)};
  text-decoration: underline;
  text-underline-offset: 3px;
  transition: opacity 0.2s;
  margin: 0 0 40px;
  text-align: center;
  display: block;
  height: 24px;
  color: rgb(20, 43, 111);
  font-weight: 500;
  letter-spacing: 0;

  &:hover {
    opacity: ${Opacity.light};
  }
`;

const Label = styled.label`
  font-size: ${rem(14)};
  font-weight: 300;
  line-height: ${rem(20)};
  margin-bottom: 8px;

  &.error {
    color: ${Color.ritualRed};
  }

  a {
    color: inherit;
    text-decoration: underline;
  }

  &.error-label {
    display: none;
    font-size: ${rem(12)};
    font-weight: 300;
    line-height: ${rem(16)};
    padding-top: 4px;

    &.error {
      display: block;
    }
  }

  .required {
    display: none;
  }
`;

const Input = styled.input.attrs({
  required: "",
})`
  height: 44px;
  width: 100%;
  color: ${Color.ritualBlue};
  display: block;
  font-size: ${rem(16)};
  line-height: ${rem(22)};
  padding: 0 1rem;
  font-weight: 300;
  background-color: #fff;
  border-radius: 0;
  border: 1px solid ${Color.ritualSecondaryDisabledBlue};
  caret-color: ${Color.ritualBlue};

  [data-whatintent="mouse"] &:focus,
  [data-whatintent="touch"] &:focus {
    outline: none;
  }

  &.error {
    border: 1px solid ${Color.ritualRed};
    color: ${Color.ritualRed};
  }
`;

export default class LoginFormComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      processing: false,
      email: "",
      emailIsValid: false,
      password: "",
      passwordIsValid: false,
      error: false,
      errorMessage: "",
    };
  }

  handleAuthenticate(e) {
    e.preventDefault();
    const { email, password } = this.state;
    const { redirect } = this.props;

    this.setState({
      processing: true,
    });

    authenticate(email, password)
      .then(() => {
        // Authentication Successful, user added to store
        // Clear error
        this.setState({
          error: false,
          errorMessage: "",
        });

        metrics.track("User Login", {
          method: "email-password",
          userHashedEmail: SHA.sha256(email),
        });

        redirectReplaceHistory(redirect);
      })
      .catch((error) => {
        if (!error.status) {
          console.error(error);
        }

        // Authentication unsuccessful
        let errorMessage = intl.t(
          "authenticate.login-error",
          "Something went wrong, try again",
        );

        if (error.status === 400) {
          errorMessage = intl.t(
            "authenticate.login-error-mismatch",
            "Email or password does not match",
          );
        }

        if (error.error === "deny") {
          metrics.track("Denial Policy Presented", {
            email,
            location: "sign-in",
          });
          errorMessage = intl.t(
            "authenticate.errors.deny-sign-in",
            "Your account has been locked due to suspicious activity. Please email customer support for further assistance.",
            {
              mailTo: getSupportMailTo("deny-sign-in", { email }),
            },
          );
        }

        this.setState({
          error: true,
          errorMessage,
        });
      })
      .finally(() => {
        this.setState({
          processing: false,
        });
      });
  }

  handleEmailValidation(e) {
    const updatedEmail = e.target.value;
    const isEmailValid = emailRegex.test(updatedEmail);

    // clear error on change
    this.setState({
      email: updatedEmail,
      isEmailValid,
      error: false,
      errorMessage: "",
    });
  }

  handlePasswordValidation(e) {
    const passwordRegex = /^.{6,}$/;
    const updatedPassword = e.target.value;
    const isPasswordValid = passwordRegex.test(updatedPassword);

    // clear error on change
    this.setState({
      password: updatedPassword,
      isPasswordValid,
      error: false,
      errorMessage: "",
    });
  }

  render() {
    let disabled = false;
    const { isEmailValid, isPasswordValid, error, errorMessage, processing } =
      this.state;

    disabled = !isEmailValid || !isPasswordValid || processing;

    const accountUrl =
      process.env.GATSBY_ACCOUNT_URL || "https://account.ritual.com";

    return (
      <Form onSubmit={this.handleAuthenticate.bind(this)}>
        <fieldset>
          <legend>
            <Text
              id="authenticate.login.sign-in-form"
              defaultMessage="Sign In Form"
            />
          </legend>
          <InputGroup className={`${error ? "error" : ""}`}>
            <Label
              htmlFor="email"
              className={`email-label ${error ? "error" : ""}`}
            >
              <Text id="authenticate.login.email" defaultMessage="Email" />
              <span className="required">
                <Text
                  id="authenticate.login.required"
                  defaultMessage="(required)"
                />
              </span>
            </Label>
            <Input
              className={`email-input ${error ? "error" : ""}`}
              id="email"
              type="text"
              autoCapitalize="off"
              autoComplete="email"
              name="email"
              value={this.state.email}
              onChange={this.handleEmailValidation.bind(this)}
            />
            <Label
              htmlFor="email"
              className={`error-label ${error ? "error" : ""}`}
              dangerouslySetInnerHTML={{ __html: errorMessage }}
            />
          </InputGroup>

          <InputGroup>
            <Label htmlFor="password">
              <Text
                id="authenticate.login.password"
                defaultMessage="Password"
              />
              <span className="required">
                <Text
                  id="authenticate.login.required"
                  defaultMessage="(required)"
                />
              </span>
            </Label>
            <Input
              className="password-input"
              id="password"
              type="password"
              name="password"
              autoCapitalize="off"
              autoComplete="current-password"
              value={this.state.password}
              onChange={this.handlePasswordValidation.bind(this)}
            />
          </InputGroup>

          <PasswordLink target="_self" href={`${accountUrl}/forgot-password`}>
            <Text
              id="authenticate.login.forgot-password"
              defaultMessage="Forgot your password?"
            />
          </PasswordLink>

          <RitualSubmitButton
            className={`submit-button fullwidth ${disabled ? "disabled" : ""}`}
            type="submit"
            name="submit"
            value={this.getSubmitButtonText()}
            title={this.getSubmitButtonText()}
          />
        </fieldset>
      </Form>
    );
  }

  getSubmitButtonText() {
    return intl.t("authenticate.login.sign-in", "Sign In");
  }
}
